import React, { useState, useRef, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./ReservationInfo.css";
import Footer from "./Footer";
import CommonPopup from "./CommonPopup";
import useInactivityRedirect from "./useInactivityRedirect";
import LoadingSpinner from "../components/LoadingSpinner";

const ReservationInfo = () => {
  const { t } = useTranslation("reservationinfo");
  const navigate = useNavigate();
  const location = useLocation();
  const { lockerNo } = useParams();
  const { mainCode } = location.state || {};
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(true);

  const phoneRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  const KIOSK_NM = sessionStorage.getItem("KIOSK_NM");

  const {
    showPopup: showInactivityPopup,
    confirmRedirect,
    cancelRedirect,
    remainingSeconds,
  } = useInactivityRedirect({
    inactivityTimeout: 2 * 60 * 1000,
    popupTimeout: 15 * 1000,
    redirectTo: "/",
  });

  useEffect(() => {

    if (!lockerNo || !mainCode || !sessionStorage.getItem("UNITCODE")) {
      alert(t("unauthorized_access"));
      navigate("/"); 
    } 

    const timer = setTimeout(() => {
		setLoading(false);
    }, 400);

    return () => clearTimeout(timer);
  }, []);

useEffect(() => {
  if (!loading && phoneRef.current) {
    phoneRef.current.focus();
  }
}, [loading]);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").substring(0, 11);
    if (digits.length < 4) return digits;
    if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  };

	const getMainCodeLabel = (mainCode) => {
		switch (mainCode) {
		  case "R":
			return t("use_type_reserve");
		  case "F":
			return t("use_type_find");
		  case "O":
			return t("use_type_open");
		  default:
			return t("use_type_unknown");
		}
	};

  const ReservationInfo_Submit = async () => {
	  const unitcode = sessionStorage.getItem("UNITCODE");

	  try {
		const statusRes = await fetch(`/api/kioskDetail/${unitcode}`);
		if (!statusRes.ok) throw new Error("KIOSK 상태 조회 실패");

		const kioskData = await statusRes.json();
		if (kioskData.KIOSK_LOGIN_ST === "N") {
		  alert(t("kiosk_offline_message"));
		  return;
		}
	  } catch (err) {
		console.error("KIOSK 상태 확인 오류:", err);
		alert(t("server_error"));
		return;
	  }
    const rawPhone = phoneNumber.replace(/\D/g, "");
    const phoneRegex = /^01[0-9]\d{7,8}$/;

    if (!rawPhone) {
      alert(t("enter_phone"));
      phoneRef.current.focus();
      return;
    }
    if (!phoneRegex.test(rawPhone)) {
      alert(t("invalid_phone"));
      phoneRef.current.focus();
      return;
    }

    if (!password.trim()) {
      alert(t("enter_password"));
      passwordRef.current.focus();
      return;
    }

    if (!confirmPassword.trim()) {
      alert(t("enter_password_confirm"));
      confirmPasswordRef.current.focus();
      return;
    }

    if (password !== confirmPassword) {
      alert(t("password_mismatch"));
      confirmPasswordRef.current.focus();
      return;
    }

    setShowPopup(true);
  };

  const handleConfirm = () => {
    const rawPhone = phoneNumber.replace(/\D/g, "");
    setShowPopup(false);
    sessionStorage.setItem("phoneNumber", rawPhone);
    sessionStorage.setItem("password", password);
    navigate("/Payment", {
      state: { mainCode, lockerNo },
    });
  };

  if (loading) return <LoadingSpinner message="Loading..." />;

  return (
    <div className="wrap">
      <header>
        <div className="title" onClick={() => navigate("/")}>
          <h1 dangerouslySetInnerHTML={{ __html: t("enter_info") }} />
        </div>
        <button className="back-button" onClick={() => window.history.back()}>
          ←
        </button>
      </header>

      <div className="reservation-info">
        <div className="reservation-info-box">
          <h2>{t("reservation_info")}</h2>
          <div className="dashed-line" />
          <div className="info-columns">
            <div className="column">
              <div className="label">{t("place")}</div>
              <div className="value">{KIOSK_NM}</div>
            </div>
            <div className="column">
              <div className="label">{t("locker_number")}</div>
              <div className="value">{lockerNo}</div>
            </div>
            <div className="column">
              <div className="label">{t("use_type")}</div>
              <div className="value">{getMainCodeLabel(mainCode)}</div>
            </div>
          </div>
        </div>
      </div>

      <main className="content" style={{ borderTop: 0}}>
        <div className="reservation-wrap">
          <div className="input-block">
            <label>{t("phone")}</label>
				<input
				  type="tel"
				  placeholder="010-0000-0000"
				  value={formatPhoneNumber(phoneNumber)}
				  onChange={(e) => {
					const value = e.target.value;
					setPhoneNumber(value);
					if (value.replace(/\D/g, "").length >= 11) {
					  passwordRef.current?.focus(); 
					}
				  }}
				  maxLength={13}
				  ref={phoneRef}
				/>
          </div>
          <div className="dashed-line" />
          <div className="input-block">
            <label>{t("password")}</label>
            <input
              type="password"
              placeholder={t("password")}
              value={password}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                console.log("입력값:", value);
                setPassword(value);
                if (value.length >= 4) {
                  confirmPasswordRef.current?.focus(); 
                }
              }}
              maxLength={4}
              ref={passwordRef}
            />
          </div>
          <div className="dashed-line" />
          <div className="input-block">
            <label>{t("password_confirm")}</label>
            <input
              type="password"
              placeholder={t("password_confirm")}
              value={confirmPassword}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setConfirmPassword(value);
              }}
              maxLength={4}
              ref={confirmPasswordRef}
            />
          </div>
        </div>
        <div className="reservationInfo-controls">
          <button className="reservationInfo-submit-button"  onClick={ReservationInfo_Submit} >
            {t("submit")}
          </button>
        </div>
      </main>

    <CommonPopup
      visible={showPopup}
      onClose={() => setShowPopup(false)}
      onConfirm={handleConfirm}
      message={`<div class="reservation-summary" style="text-align: left;">
        <h2 style="text-align: center; margin-bottom: 1em;">${t("reservation_info")}</h2>
        <p style="margin: .5rem 0 0.5rem;"><strong>${t("place")}:</strong> ${KIOSK_NM}</p>
        <p style="margin: .5rem 0 0.5rem;"><strong>${t("locker_number")}:</strong> ${lockerNo}</p>
        <p style="margin: .5rem 0 0.5rem;"><strong>${t("use_type")}:</strong> ${getMainCodeLabel(mainCode)}</p>
        <p style="margin: .5rem 0 0.5rem;"><strong>${t("phone")}:</strong> ${formatPhoneNumber(phoneNumber)}</p>
      </div>`}
    />

      {showInactivityPopup && (
        <CommonPopup
          visible={true}
          message={`${t("continue_question")}<br/><span style="font-weight:700;color:red">(${remainingSeconds} ${t("seconds")})</span>${t("auto_redirect")}`}
          onConfirm={confirmRedirect}
          onClose={cancelRedirect}
          confirmText={t("yes")}
          cancelText={t("no")}
        />
      )}

      <Footer />
    </div>
  );
};

export default ReservationInfo;
